"use client";

import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/app/_module/components/ui/dialog";
import { Button } from "@/app/_module/components/ui/button";
import { Label } from "@/app/_module/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/app/_module/components/ui/radio-group";
import { Textarea } from "@/app/_module/components/ui/textarea";
import { useToast } from "@/app/_module/components/ui/use-toast";
import { forumService } from "./ForumService";

const REASONS = [
  { value: "spam", label: "Spam or repetitive content" },
  { value: "harassment", label: "Harassment or disrespect" },
  { value: "pii", label: "Client PII / compliance risk" },
  { value: "misinformation", label: "Misleading product or carrier claims" },
  { value: "self_promotion", label: "Excessive self-promotion / recruiting" },
  { value: "other", label: "Something else" },
];

export function ReportDialog({
  open,
  onOpenChange,
  targetType,
  targetId,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  targetType: "post" | "comment";
  targetId: string;
}) {
  const [reason, setReason] = useState("spam");
  const [details, setDetails] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();

  const submit = async () => {
    setSubmitting(true);
    try {
      const res = await forumService.report({ targetType, targetId, reason, details: details || undefined });
      toast({
        title: res.alreadyReported ? "Already reported" : "Report submitted",
        description: res.alreadyReported
          ? "You've already reported this — our team is on it."
          : "Thanks — our team will review it.",
        duration: 4000,
      });
      onOpenChange(false);
      setDetails("");
    } catch (e: any) {
      toast({ title: "Report failed", description: e.message, variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" onClick={(e) => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle>Report {targetType}</DialogTitle>
          <DialogDescription>
            Reports are reviewed by the InsuraCentral team.
          </DialogDescription>
        </DialogHeader>
        <RadioGroup value={reason} onValueChange={setReason} className="gap-2">
          {REASONS.map((r) => (
            <div key={r.value} className="flex items-center space-x-2">
              <RadioGroupItem value={r.value} id={`report-${r.value}`} />
              <Label htmlFor={`report-${r.value}`} className="text-sm font-normal cursor-pointer">
                {r.label}
              </Label>
            </div>
          ))}
        </RadioGroup>
        <Textarea
          placeholder="Anything else we should know? (optional)"
          value={details}
          onChange={(e) => setDetails(e.target.value.slice(0, 500))}
          rows={2}
          className="text-sm"
        />
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="destructive" size="sm" onClick={submit} disabled={submitting}>
            {submitting ? "Submitting…" : "Submit report"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
